import { getCase } from "../../content/lookups.js";
import { createCardInstance } from "../deck/createInstance.js";
import { discardPlayed } from "../run/discardPlayed.js";
import { advanceMap, finishNode } from "../run/finishNode.js";
import { pickOrganizations } from "../rewards/pickOrganizations.js";
import { pickRewards } from "../rewards/pickRewards.js";
import type { Command, GameState } from "../types/index.js";
import { appendLog } from "../run/log.js";
import { accept, reject } from "./reject.js";
import { finishCaseCards } from "../run/caseCards.js";

export function handleChooseReward(
  state: GameState,
  command: Extract<Command, { type: "CHOOSE_REWARD" }>,
): GameState {
  if (state.screen !== "reward.card" || !state.run?.activeCase) {
    return reject(state, "カード報酬画面でのみ選択できる");
  }

  const nodeId = state.run.activeCase.nodeId;
  const caseDef = getCase(state.run.activeCase.caseId);
  if (!caseDef) return reject(state, "未知の案件");
  let run = discardPlayed(state.run);

  if (command.cardId === "recover") {
    const recovery = caseDef.size === "large" ? run.fatigue : caseDef.size === "medium" ? 3 : 2;
    const recovered = Math.min(run.fatigue, recovery);
    run = appendLog(
      { ...run, fatigue: Math.max(0, run.fatigue - recovery) },
      {
        phase: state.run.activeCase.phase,
        kind: "reward",
        message: `報酬で疲労を ${recovered} 回復`,
      },
    );
  } else {
    if (!state.run.rewardCardIds.includes(command.cardId)) {
      return reject(state, "候補外のカード");
    }
    const created = createCardInstance(
      { nextCardSequence: run.nextCardSequence },
      command.cardId,
    );
    run = {
      ...run,
      discard: [...run.discard, created.instance],
      nextCardSequence: created.nextCardSequence,
    };
    run = appendLog(run, {
      phase: state.run.activeCase.phase,
      kind: "reward",
      message: `報酬カードを取得`,
    });
  }

  run = finishNode(run, nodeId);

  return accept({
    ...state,
    screen: "map",
    run: { ...run, rewardCardIds: [], organizationChoices: [] },
    resolution: null,
  });
}

export function handleChooseOrganization(
  state: GameState,
  command: Extract<Command, { type: "CHOOSE_ORGANIZATION" }>,
): GameState {
  if (state.screen !== "reward.organization" || !state.run) {
    return reject(state, "組織基盤報酬画面でのみ選択できる");
  }
  if (!state.run.organizationChoices.includes(command.organizationId)) {
    return reject(state, "候補外の組織基盤");
  }

  const run = {
    ...state.run,
    organizations: [...state.run.organizations, command.organizationId],
    organizationChoices: [],
  };

  if (!state.run.activeCase) {
    return accept({ ...state, screen: "map", run: finishNode(run, state.run.currentNodeId) });
  }

  return accept({ ...state, screen: "reward.card", run });
}

export function setupSuccessRewards(state: GameState): GameState {
  if (!state.run?.activeCase) return state;

  const caseDef = getCase(state.run.activeCase.caseId);
  if (!caseDef) return state;
  const nodeId = state.run.activeCase.nodeId;
  let run = advanceMap(finishCaseCards(discardPlayed(state.run)), nodeId);

  if (caseDef.isBoss) {
    run = finishNode(run, nodeId);
    return accept({
      ...state,
      screen: "result.clear",
      run: { ...run, outcome: "clear" },
      resolution: null,
    });
  }

  if (caseDef.grantsOrganization) {
    const orgPick = pickOrganizations({
      owned: run.organizations,
      rngState: run.rngState,
      stageId: run.stageId,
    });
    const rewardPick = pickRewards({ size: "large", rngState: orgPick.rngState, stageId: run.stageId });
    run = {
      ...run,
      rngState: rewardPick.rngState,
      organizationChoices: orgPick.organizationIds,
      rewardCardIds: rewardPick.cardIds,
    };
    if (orgPick.organizationIds.length === 0) {
      return accept({
        ...state,
        screen: "reward.card",
        run,
        resolution: null,
      });
    }
    return accept({
      ...state,
      screen: "reward.organization",
      run,
      resolution: null,
    });
  }

  const poolSize =
    caseDef.size === "small" || caseDef.size === "medium" || caseDef.size === "large"
      ? caseDef.size
      : "small";
  const rewardPick = pickRewards({ size: poolSize, rngState: run.rngState, stageId: run.stageId });
  run = {
    ...run,
    rngState: rewardPick.rngState,
    rewardCardIds: rewardPick.cardIds,
  };

  return accept({
    ...state,
    screen: "reward.card",
    run,
    resolution: null,
  });
}

export function setupFailure(state: GameState): GameState {
  if (!state.run?.activeCase) return state;
  const nodeId = state.run.activeCase.nodeId;
  let run = discardPlayed(state.run);
  run = finishNode(run, nodeId);
  return accept({ ...state, screen: "map", run, resolution: null });
}

export function setupGameover(state: GameState): GameState {
  if (!state.run) return state;
  const endedNodeId = state.run.activeCase?.nodeId ?? state.run.currentNodeId;
  const run = finishCaseCards(discardPlayed(state.run));
  return accept({
    ...state,
    screen: "result.gameover",
    run: { ...run, currentNodeId: endedNodeId, activeCase: null, outcome: "gameover" },
    resolution: null,
  });
}
